<template>
  <div>
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="getString('action.toOverview')"
          @click="$router.push(`/${module}`)"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn  style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="$v.data.$anyError ? 'grey-5' : 'ctrl-turquoise'"
          :disable="$v.data.$anyError"
          :label="getString('action.save')"
          @click="save()"
          icon-right="save"
        />
        <q-btn v-if="data.id"
          class="q-ml-md capitalize ctrl-button right"
          icon-right="delete"
          color="negative capitalize"
          :label="getString('action.delete')"
          align="right"
          @click="deleteCategory()"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">
      <!-- Left -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ 'Category Details' }}
            </p>

            <div class="card-block">
              <div class="row">
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('name')"
                  @input="validateLabel"
                  :error="nameSet && !$v.data.name.required"
                  v-model="data.name"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('url')"
                  v-model="data.url"
                />
              </div>
            </div>

            <template v-if="$route.params.id">
              <div class="q-mt-md">
                <p class="card-title">
                  Category usage
                </p>

                <table-overview
                  :endpoint="`category/${$route.params.id}/usage`"
                  :responseFormat="'posts'"
                  :columns="columns"
                  permission="category.view"
                  customEvents
                  @click="goToPost"
                  module="category"
                  :selectable="false">
                </table-overview>
              </div>
            </template>

          </div>
        </div>
      </div>
      <!-- Right -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pl-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              Meta
            </p>

            <div class="card-block">
              <div class="row">
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.seo.title')"
                  v-model="data.meta.seo.title"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.seo.description')"
                  v-model="data.meta.seo.description"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.og.title')"
                  v-model="data.meta.og.title"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.og.description')"
                  v-model="data.meta.og.description"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.og.image')"
                  readonly
                  v-model="ogImage"
                  @click="imageModal = true"
                />
                <q-input
                  type="text"
                  class="full-width q-mb-md"
                  :float-label="getString('editor.meta.canonical')"
                  v-model="data.meta.canonical"
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Image select modal -->
    <q-modal no-esc-dismiss v-if="imageModal" v-model="imageModal">
      <div class="row" style="display: flex; justify-content: flex-end">
        <q-btn
          icon="close"
          @click="imageModal = false"
          />
      </div>
      <div style="height: 75vh; width: 80vw;">
        <div class="row q-pa-md">
          <div class="col-7 q-pa-md">
            <library-table :guid="guid" :readonly="true" spacing="45vh"/>
          </div>
          <div class="col-5 q-pa-md">
            <div>
              <image-preview :guid="guid"/>
            </div>
          </div>
        </div>
      </div>
    </q-modal>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import EventBus from '@/event-bus';

// Mixins
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import suggestUrl from '@/mixins/suggestUrl';

import createUuid from '@/helpers/createUuid';
import getString from '@/helpers/GetStrings';

// Components
import LibraryTable from '@/components/library/LibraryTable.vue';
import ImagePreview from '@/components/library/ImagePreview.vue';
import TableOverview from '@/components/overview/TableOverview.vue';
import common from '../common';

export default {
  mixins: [
    common,
    suggestUrl,
    fetchDetail,
  ],
  components: {
    LibraryTable,
    ImagePreview,
    TableOverview,
  },
  validations: {
    data: {
      name: {
        required,
      },
    },
  },
  data() {
    return {
      columns: [
        {
          name: '__slot:nav-title',
          title: getString('title'),
          sortField: 'title',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m user text-ellipsis',
        },
        {
          name: 'type',
          title: 'Type',
          sortField: 'title',
          callback: val => getString(`module.name.${val}`),
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m user text-ellipsis',
        },
      ],
      guid: createUuid(),
      imageModal: false,
      containsErrors: true,
      data: {
        id: null,
        name: '',
        url: '',
        meta: {
          seo: {
            description: '',
            title: '',
          },
          og: {
            description: '',
            title: '',
            image: {
              location: '',
            },
          },
          canonical: '',
        },
      },
      nameSet: false,
    };
  },

  methods: {
    getString,
    validateLabel(input) {
      this.suggestUrl(input);
      this.nameSet = true;
      this.$v.$touch();
    },

    // Go to the selected post of the tag
    goToPost(data) {
      if (!data.row.id || !data.row.type) {
        return;
      }
      this.$router.push(`/${data.row.type}/${data.row.id}`);
    },

    deleteCategory() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: `*Delete category - "${this.data.name}"?`,
        ok: getString('dialog.confirm.ok'),
        cancel: getString('dialog.confirm.cancel'),
      }).then(() => {
        // delete call
        this.axios.delete(`${this.endpoint}/${this.data.id}`)
          .then(() => {
            // Refresh data to see the result
            this.$q.notify({
              message: '*Category removed.',
              position: 'top-right',
              timeout: 1000,
              color: 'positive',
            });
            this.$router.push('/categories');
          })
          .catch((error) => {
            // Error
            console.warn(error);
          });
      }).catch(() => {
        console.warn('Not removed');
      });
    },

    save() {
      let params;

      const requiredFields = [
        `name=${this.data.name}`,
        this.data.url ? `url=${this.data.url}` : null,
      ];

      const meta = [
        typeof this.data.meta.seo.title === 'string' ? `meta_seo_title=${this.data.meta.seo.title}` : null,
        typeof this.data.meta.seo.description === 'string' ? `meta_seo_description=${this.data.meta.seo.description}` : null,
        typeof this.data.meta.og.title === 'string' ? `meta_og_title=${this.data.meta.og.title}` : null,
        typeof this.data.meta.og.description === 'string' ? `meta_og_description=${this.data.meta.og.description}` : null,
        this.data.meta.og.image && typeof this.data.meta.og.image.location === 'string' ? `meta_og_image=${this.data.meta.og.image.location.replace('api/', '')}` : null,
        typeof this.data.meta.canonical === 'string' ? `meta_canonical=${this.data.meta.canonical}` : null,
      ];

      // Check if adding or modifying
      if (!this.data.id) {
        params = requiredFields.filter(val => !!val).join('&');
        // Adding a new category... First post name and url
        this.axios.post('category', params).then(async () => {
          // If either of the meta fields have been entered, send these as well.
          if (this.data.meta.description || this.data.meta.keywords) {
            params = requiredFields.concat(meta).filter(val => !!val).join('&');
            await this.axios.put('category', params);
          }
          this.$q.notify({
            message: `*Added a new category - "${this.data.name}"`,
            timeout: 2000,
            type: 'positive',
            position: 'top-right',
          });
        });
      } else {
        // The category already exists, update with new values
        params = requiredFields.concat(meta).join('&');
        this.axios.put(`category/${this.data.id}`, params).then(() => {
          this.$q.notify({
            message: `*Modified category - "${this.data.name}"`,
            timeout: 2000,
            type: 'positive',
            position: 'top-right',
          });
        });
      }
    },
  },

  computed: {
    ogImage() {
      return this.data.meta.og.image && this.data.meta.og.image.name;
    },
  },

  mounted() {
    if (this.$route.params.id) {
      this.axios.get(`category/${this.$route.params.id}`).then((res) => {
        this.data = res.data.category;
      });
    }

    EventBus.$on(`file-select:${this.guid}`, (data) => {
      this.data.meta.og.image = { location: data.location, name: data.name };
      this.imageModal = false;
    });
  },
};
</script>

<style lang="stylus" scoped>

</style>
